import { isEmptyValue, isNotEmptyValue } from "kwikid-toolkit";
import {
  EKwikUIInputCurrencyType,
  TKwikUIInputCurrencyType
} from "./input-currency.definitions";

const formattedValue = (formattedValue, originalValue, decimalPart) => {
  formattedValue = formattedValue.replace(/-/g, "");

  if (originalValue.includes(".")) {
    formattedValue += ".";
  }

  if (isNotEmptyValue(decimalPart)) {
    formattedValue += decimalPart;
  }

  if (originalValue.startsWith("-") && originalValue[1] == ".") {
    formattedValue = `-0${formattedValue}`;
  } else if (originalValue.startsWith("-") && originalValue[1] !== ".") {
    formattedValue = `-${formattedValue}`;
  } else if (!originalValue.startsWith("-") && originalValue[0] == ".") {
    formattedValue = `0${formattedValue}`;
  }

  return formattedValue;
};

/**
 * Removes leading zeros from a numeric string.
 * @param value - The numeric string.
 * @returns - The value without leading zeros.
 */
const removeLeadingZeros = (value: string): string => {
  if (value.startsWith("-")) {
    return `-${value.replace(/^(-?)(0+)(\d+)/, "$1$3")}`;
  }
  if (isEmptyValue(value)) {
    return "";
  }
  return value.replace(/^0+/, "") || "0";
};

export const formatToINR = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(".");
  const integerPart = removeLeadingZeros(integerPartRaw);

  let lastThree = integerPart.slice(-3);
  const otherNumbers = integerPart.slice(0, -3);
  if (isNotEmptyValue(otherNumbers)) {
    lastThree = `,${lastThree}`;
  }

  const formatted =
    otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",") + lastThree;

  return formattedValue(formatted, value, decimalPart);
};

export const formatToUSD = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(".");
  const integerPart = removeLeadingZeros(integerPartRaw);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  return formattedValue(formattedInteger, value, decimalPart);
};

export const formatToEUR = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(",");
  const integerPart = removeLeadingZeros(integerPartRaw);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return formattedValue(formattedInteger, value, decimalPart);
};

export const formatToGBP = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(".");
  const integerPart = removeLeadingZeros(integerPartRaw);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedValue(formattedInteger, value, decimalPart);
};

export const formatToJPY = (value: string): string => {
  const formattedInteger = removeLeadingZeros(
    value.replace("-", "").replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  );
  return formattedInteger;
};

export const formatToCNY = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(".");
  const integerPart = removeLeadingZeros(integerPartRaw);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedValue(formattedInteger, value, decimalPart);
};

export const formatToAUD = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(".");
  const integerPart = removeLeadingZeros(integerPartRaw);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedValue(formattedInteger, value, decimalPart);
};

export const formatToCAD = (value: string): string => {
  const [integerPartRaw, decimalPart] = value.replace(/-/g, "").split(".");
  const integerPart = removeLeadingZeros(integerPartRaw);
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return formattedValue(formattedInteger, value, decimalPart);
};

export const formatCurrency = (
  currencyType: TKwikUIInputCurrencyType,
  value: string
): string => {
  switch (currencyType) {
    case EKwikUIInputCurrencyType.INR:
      return formatToINR(value);
    case EKwikUIInputCurrencyType.USD:
      return formatToUSD(value);
    case "EUR":
      return formatToEUR(value);
    case "GBP":
      return formatToGBP(value);
    case "JPY":
      return formatToJPY(value);
    case "CNY":
      return formatToCNY(value);
    case "AUD":
      return formatToAUD(value);
    case "CAD":
      return formatToCAD(value);
    default:
      return value;
  }
};
